import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { HOME_ROUTE } from '@/config';
import useApi from '@/hooks/useApi';
import useEmailFieldValidation from '@/hooks/useEmailFieldValidation';
import useLangFromQuery from '@/hooks/useLangFromQuery';
import useApp from '@/pages/App.context';
import useCountryWithCertainty, {
  Certainty,
} from '@/hooks/useCountryWithCertainty';
import Button from '@/components/Button';
import FormInput from '@/components/FormInput';
import FormPassword from '@/components/FormPassword';
import FormError from '@/components/FormError';
import initiateIDAustriaLogin from '../initiateIDAustriaLogin.utils';
import parseContinueUrl from '@/utils/parseContinueUrl.util';
import IcIDAustriaKey from '@/assets/icons/IDAustriaKey.svg?react';
import IcIDAustriaText from '@/assets/icons/IDAustriaText.svg?react';
import IcInfoCircled from '@/assets/icons/InfoCircled.svg?react';
import styles from '@/pages/Auth/Auth.module.scss';

type LoginInputs = {
  email: string;
  password: string;
};

const Login = () => {
  const { t } = useTranslation(['auth']);
  const { setAppTitle } = useApp();
  useLangFromQuery();
  const [loading, setLoading] = useState(false);
  const [login] = useApi('users.login');
  const location = useLocation();
  const region = useCountryWithCertainty();

  const currentSearchParams = new URLSearchParams(location.search);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<LoginInputs>();

  const { emailRegistration, handleEmailBlur, handleEmailChange } =
    useEmailFieldValidation<LoginInputs>({
      fieldName: 'email',
      register,
      requiredMessage: t('auth:fields.emailRequired'),
      patternMessage: t('auth:fields.emailValidity'),
      setValue,
      trigger,
    });

  const navigate = useNavigate();

  // submit form handler
  const doLogin: SubmitHandler<LoginInputs> = async (formInputs) => {
    setLoading(true);

    const values = {
      email: formInputs.email,
      password: formInputs.password,
    };

    const result = await login(values);

    if (!result) {
      return;
    }

    if (result.error?.statusCode === 401) {
      setError('root', { message: result.error.response.message });
      setLoading(false);
      return;
    }

    // Any other failure (e.g. 429 from the login rate limiter) must not fall
    // through to the success path, which would store a bogus token and
    // navigate on as if the login had worked.
    if (result.error) {
      const isRateLimited = result.error.statusCode === 429;
      setError('root', {
        message: isRateLimited
          ? t('auth:login.tooManyAttempts')
          : t('auth:unknownError'),
      });
      setLoading(false);
      return;
    }

    const token = result.response?.token ?? 'not-a-valid-token';
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token); // In case this login is from signing flow

    // check the continue-variable — only same-origin, non-auth paths are allowed
    const continuePath = parseContinueUrl(currentSearchParams.get('continue'));
    if (continuePath) {
      void navigate(continuePath, { replace: true });
      return;
    }

    if (!result.response?.emailVerified) {
      void navigate('/auth/register/verify-email', {
        replace: true,
        state: { email: formInputs.email },
      });
      return;
    }

    if (!result.response?.accountTypeSelected) {
      void navigate('/auth/register/account-type', { replace: true });
      return;
    }

    void navigate(HOME_ROUTE);
  };

  useEffect(() => {
    const err = new URLSearchParams(location.search).get('error');

    if (err === 'age') {
      setError('root', {
        message: t('auth:login.idaustria.errors.age'),
      });
    }
    if (err === 'general') {
      setError('root', { message: t('auth:login.idaustria.errors.general') });
    }
  }, [location.search, setError, t]);

  useEffect(() => {
    setAppTitle(t('auth:login.title'));
  }, [setAppTitle, t]);

  const showIDAustriaOption =
    !region.country ||
    (region.country === 'DE' && region.certainty === Certainty.LOW) ||
    region.country === 'AT';

  // When a known signer is bounced here from a signing link, the `continue`
  // target points back to the `/sign/` flow with `postLogin=true`. Show a short
  // note so the user understands why they have to sign in first.
  const continuePath = parseContinueUrl(currentSearchParams.get('continue'));
  const isSignContinuation =
    !!continuePath &&
    continuePath.includes('/sign/') &&
    continuePath.includes('postLogin=true');

  return (
    <>
      <h2 className={styles.heading}>{t('auth:login.title')}</h2>
      <div className={styles.text}>{t('auth:login.desc')}</div>
      {isSignContinuation && (
        <div className={styles.infoBox} role="status">
          <IcInfoCircled className={styles.infoBoxIcon} aria-hidden="true" />
          <span>{t('auth:login.signToContinue')}</span>
        </div>
      )}
      <form onSubmit={handleSubmit(doLogin)} className={styles.form}>
        <FormInput
          autoComplete="email"
          errorMessage={errors.email?.message}
          id="email"
          label={t('auth:fields.email')}
          placeholder={t('auth:fields.emailPlaceholder')}
          type="email"
          {...emailRegistration}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
        />
        <FormPassword
          autoComplete="current-password"
          errorMessage={errors.password?.message}
          id="password"
          label={t('auth:fields.password')}
          placeholder={t('auth:fields.passwordPlaceholder')}
          {...register('password')}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} />
        )}
        <Button className={styles.button} submit loading={loading}>
          {t('auth:login.submit')}
        </Button>
        {showIDAustriaOption && (
          <>
            <div className={styles.divider}>{t('auth:login.separator')}</div>
            <Button
              className={classNames(
                styles.button,
                styles.external,
                styles.idAustria
              )}
              disabled={loading}
              variant="secondary"
              onClick={initiateIDAustriaLogin}
              aria-label={t('auth:login.button.external', {
                name: 'IDAustria',
              })}
            >
              <IcIDAustriaKey className={styles.idAustriaLogo} />
              <IcIDAustriaText className={styles.idAustriaText} />
            </Button>
          </>
        )}
        <div className={styles.switchToOther}>
          <Trans i18nKey="auth:login.switchQuestion">
            <Link to="/auth/register" className={styles.link} />
          </Trans>
        </div>
        <Button
          as="link"
          className={classNames(styles.button, styles.forgot)}
          variant="tertiary"
          to="/auth/forgotten-password"
        >
          {t('auth:login.forgotten')}
        </Button>
      </form>
    </>
  );
};

export default Login;
